import { EnvironmentOutlined } from "@ant-design/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Alert,
  App,
  Button,
  Card,
  Checkbox,
  Divider,
  Form,
  Input,
  Modal,
  Select,
  Spin,
  Typography,
} from "antd";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { ApiError, apiFetch } from "../api/client";
import { useServerNotifications } from "../api/notifications";
import type {
  CongregationSettingsResponse,
  CongregationSettingsUpdateResponse,
  SettingRange,
} from "../api/types";
import { CountrySelect } from "../components/CountrySelect";

interface CongregationSettingsValues {
  denomination: string;
  congregation_code: string;
  congregation_phone: string;
  street_number: string;
  zip_code: string;
  city: string;
  state: string;
  country: string;
  contact_first_name: string;
  contact_last_name: string;
  phone: string;
  mobile: string;
  email: string;
  default_week_day: number | null;
  default_day_time: string;
  max_concurrent_speaker_assigned: number | null;
  apply_on_absence_limitation_only: boolean;
  rewind_weeks: number | null;
  forward_weeks: number | null;
  hide_events_after_months: number | null;
  provides_hospitality: boolean;
}

function optionsOf(range: SettingRange | undefined) {
  if (!range) return [];

  const options = [];
  for (let value = range.min; value <= range.max; value += range.step ?? 1) {
    options.push({ value, label: String(value) });
  }
  return options;
}

/**
 * Sidebar entry "congregation_settings" → /congregation/congregation_settings.
 *
 * The congregation's own configuration: its address, its contact person, the
 * default day and time of the public talk, and how many of its speakers may be
 * away at once. Needs full access — the coordinator's personal settings are a
 * separate screen an observer may edit.
 *
 * Saving the address makes the server geocode it, and it answers with a link to
 * the map so the coordinator can check where the congregation ended up. That one
 * is not shown like the other messages. A toast fades after a few seconds, and
 * nobody clicks a link that is already gone; a banner at the top of the form is
 * off-screen at the moment it appears, because the save button sits at the
 * bottom of a long form. So it is a dialog, in front of whatever the reader was
 * looking at, and it waits.
 *
 * That is not an ornament: the coordinates come out of what was typed, and every
 * distance to a speaker is measured from them. A pin in the wrong village is not
 * visible anywhere on this screen. The dialog only appears when the address
 * really changed, which for a congregation is a rare day.
 */
export function CongregationSettingsPage() {
  const { t } = useTranslation();
  const notify = useServerNotifications();
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const [form] = Form.useForm<CongregationSettingsValues>();
  const [addressCheck, setAddressCheck] =
    useState<CongregationSettingsUpdateResponse["address_check"]>(null);

  const settings = useQuery({
    queryKey: ["congregation", "settings"],
    queryFn: () => apiFetch<CongregationSettingsResponse>("/congregation/settings"),
  });

  useEffect(() => {
    if (!settings.data) return;

    const congregation = settings.data.data.congregation;
    form.setFieldsValue({
      denomination: congregation.denomination ?? "",
      congregation_code: congregation.congregation_code ?? "",
      congregation_phone: congregation.congregation_phone ?? "",
      street_number: congregation.street_number ?? "",
      zip_code: congregation.zip_code ?? "",
      city: congregation.city ?? "",
      state: congregation.state ?? "",
      country: congregation.country ?? "",
      contact_first_name: congregation.contact_first_name ?? "",
      contact_last_name: congregation.contact_last_name ?? "",
      phone: congregation.phone ?? "",
      mobile: congregation.mobile ?? "",
      email: congregation.email ?? "",
      default_week_day: congregation.default_week_day,
      default_day_time: congregation.default_day_time ?? "",
      max_concurrent_speaker_assigned: congregation.max_concurrent_speaker_assigned,
      apply_on_absence_limitation_only: congregation.apply_on_absence_limitation_only,
      rewind_weeks: congregation.rewind_weeks,
      forward_weeks: congregation.forward_weeks,
      hide_events_after_months: congregation.hide_events_after_months,
      provides_hospitality: congregation.provides_hospitality,
    });
  }, [settings.data, form]);

  const update = useMutation({
    mutationFn: (values: CongregationSettingsValues) =>
      apiFetch<CongregationSettingsUpdateResponse>("/congregation/settings", {
        method: "PUT",
        body: values,
      }),
    onSuccess: (data) => {
      notify(data.notifications);
      setAddressCheck(data.address_check);
      void queryClient.invalidateQueries({ queryKey: ["congregation", "settings"] });
      // The same record backs the contact screen and the zone coordinator's list.
      void queryClient.invalidateQueries({ queryKey: ["congregation", "contacts"] });
      void queryClient.invalidateQueries({ queryKey: ["circumscription", "congregations"] });
    },
    onError: (error: unknown) => {
      if (error instanceof ApiError && error.notifications) {
        notify(error.notifications);
        return;
      }
      void message.error(error instanceof Error ? error.message : String(error));
    },
  });

  const data = settings.data?.data;
  const config = data?.config;

  const rewindWeeks = useMemo(
    () => optionsOf(config && { min: config.min_rewind_weeks, max: config.max_rewind_weeks }),
    [config],
  );
  const forwardWeeks = useMemo(
    () =>
      optionsOf(
        config && {
          min: config.min_forward_weeks,
          max: config.max_forward_weeks,
          step: config.forward_weeks_step,
        },
      ),
    [config],
  );
  const hideAfterMonths = useMemo(
    () =>
      optionsOf(config && { min: config.min_hide_after_months, max: config.max_hide_after_months }),
    [config],
  );
  // One entry per speaker the congregation has: it cannot lend out more.
  const concurrentSpeakers = useMemo(
    () => optionsOf(data && { min: 1, max: data.speaker_count }),
    [data],
  );

  if (settings.isPending) return <Spin />;

  if (settings.error) {
    return (
      <Alert
        type="error"
        showIcon
        title={
          settings.error instanceof Error ? settings.error.message : String(settings.error)
        }
      />
    );
  }

  const weekDays = data?.week_days ?? [];
  const hint = (text: string) => <Typography.Text type="secondary">{text}</Typography.Text>;

  return (
    <Card title={t("navigation.congregation_settings")} style={{ maxWidth: 720 }}>
      <Modal
        open={addressCheck !== null}
        title={t("congregation.headers.address")}
        onCancel={() => setAddressCheck(null)}
        onOk={() => setAddressCheck(null)}
        okText={t("dialog.confirm")}
        // Nothing to cancel — the address is saved either way, this only asks
        // for a look at it.
        cancelButtonProps={{ style: { display: "none" } }}
      >
        <Typography.Paragraph>{addressCheck?.message}</Typography.Paragraph>
        <Button
          type="primary"
          icon={<EnvironmentOutlined />}
          href={addressCheck?.map_url}
          target="_blank"
          rel="noreferrer"
        >
          {t("link.googlemap")}
        </Button>
      </Modal>

      <Form<CongregationSettingsValues>
        form={form}
        layout="vertical"
        requiredMark={false}
        onFinish={(values) => update.mutate(values)}
      >
        <Divider titlePlacement="start">
          {t("congregation.headers.address")}
        </Divider>

        <Form.Item
          name="denomination"
          label={t("activerecord.attributes.congregation.denomination")}
          rules={[{ required: true }]}
        >
          <Input />
        </Form.Item>

        <Form.Item
          name="congregation_code"
          label={t("activerecord.attributes.congregation.congregation_code")}
          rules={[{ required: true }]}
        >
          <Input style={{ width: 140 }} />
        </Form.Item>

        <Form.Item
          name="congregation_phone"
          label={t("activerecord.attributes.congregation.congregation_phone")}
        >
          <Input />
        </Form.Item>

        <Form.Item
          name="street_number"
          label={t("activerecord.attributes.congregation.street_number")}
        >
          <Input />
        </Form.Item>

        <Form.Item name="zip_code" label={t("activerecord.attributes.congregation.zip_code")}>
          <Input style={{ width: 140 }} />
        </Form.Item>

        <Form.Item name="city" label={t("activerecord.attributes.congregation.city")}>
          <Input />
        </Form.Item>

        <Form.Item name="state" label={t("activerecord.attributes.congregation.state")}>
          <Input style={{ width: 200 }} />
        </Form.Item>

        <Form.Item name="country" label={t("activerecord.attributes.congregation.country")}>
          <CountrySelect style={{ width: 240 }} />
        </Form.Item>

        <Divider titlePlacement="start">
          {t("congregation.headers.contact_person")}
        </Divider>

        <Form.Item
          name="contact_first_name"
          label={t("activerecord.attributes.congregation.contact_first_name")}
        >
          <Input />
        </Form.Item>

        <Form.Item
          name="contact_last_name"
          label={t("activerecord.attributes.congregation.contact_last_name")}
        >
          <Input />
        </Form.Item>

        <Form.Item name="phone" label={t("activerecord.attributes.congregation.phone")}>
          <Input />
        </Form.Item>

        <Form.Item name="mobile" label={t("activerecord.attributes.congregation.mobile")}>
          <Input />
        </Form.Item>

        <Form.Item
          name="email"
          label={t("activerecord.attributes.congregation.email")}
          rules={[{ type: "email" }]}
        >
          <Input />
        </Form.Item>

        <Divider titlePlacement="start">
          {t("congregation.headers.settings")}
        </Divider>

        <Form.Item
          name="default_week_day"
          label={t("activerecord.attributes.congregation.default_week_day")}
          extra={hint(t("congregation.hints.default_week_day"))}
        >
          <Select options={weekDays} style={{ width: 200 }} allowClear />
        </Form.Item>

        <Form.Item
          name="default_day_time"
          label={t("activerecord.attributes.congregation.default_day_time")}
          extra={hint(t("congregation.hints.default_day_time"))}
          rules={[
            {
              pattern: /^([01]\d|2[0-3]):[0-5]\d$/,
              message: t(
                "activerecord.errors.models.congregation.attributes.default_day_time.invalid_format",
              ),
            },
          ]}
        >
          <Input placeholder="hh:mm" style={{ width: 140 }} />
        </Form.Item>

        <Form.Item
          name="max_concurrent_speaker_assigned"
          label={t("activerecord.attributes.congregation.max_concurrent_speaker_assigned")}
          extra={hint(t("congregation.hints.max_concurrent_speaker_assigned"))}
        >
          <Select options={concurrentSpeakers} style={{ width: 120 }} />
        </Form.Item>

        <Form.Item
          name="apply_on_absence_limitation_only"
          valuePropName="checked"
          extra={hint(t("congregation.hints.apply_on_absence_limitation_only"))}
        >
          <Checkbox>
            {t("activerecord.attributes.congregation.apply_on_absence_limitation_only")}
          </Checkbox>
        </Form.Item>

        <Form.Item
          name="rewind_weeks"
          label={t("activerecord.attributes.congregation.rewind_weeks")}
          extra={hint(t("congregation.hints.rewind_weeks"))}
        >
          <Select options={rewindWeeks} style={{ width: 120 }} />
        </Form.Item>

        <Form.Item
          name="forward_weeks"
          label={t("activerecord.attributes.congregation.forward_weeks")}
          extra={hint(t("congregation.hints.forward_weeks"))}
        >
          <Select options={forwardWeeks} style={{ width: 120 }} />
        </Form.Item>

        <Form.Item
          name="hide_events_after_months"
          label={t("activerecord.attributes.congregation.hide_events_after_months")}
          extra={hint(t("congregation.hints.hide_events_after_months"))}
        >
          <Select options={hideAfterMonths} style={{ width: 120 }} />
        </Form.Item>

        <Divider titlePlacement="start">
          {t("congregation.headers.course_of_hospitality")}
        </Divider>

        <Form.Item name="provides_hospitality" valuePropName="checked">
          <Checkbox>
            {t("activerecord.attributes.congregation.provides_hospitality")}
          </Checkbox>
        </Form.Item>

        <Button type="primary" htmlType="submit" loading={update.isPending}>
          {t("form.update")}
        </Button>
      </Form>
    </Card>
  );
}
